import { motion } from "framer-motion";
import {
  FileText,
  Database,
  MessageSquare,
  Share2,
  ShieldCheck,
  Cookie,
  UserCheck,
  Settings2,
  RefreshCw,
  Mail,
  Clock,
  Globe,
} from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (delay = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: "easeOut", delay },
  }),
};

// Helper to bold text before a colon (e.g., "Personal Information: ...")
const boldPrefix = (text) => {
  const match = text.match(/^(.*?):\s*(.*)$/);
  if (match) {
    return (
      <>
        <strong>{match[1]}:</strong> {match[2]}
      </>
    );
  }
  return text;
};

// Helper to bold specific key phrases in extra text
const formatExtra = (text) => {
  const parts = text.split(/(?<=\.)\s*/);
  return parts.map((part, i) => {
    const trimmed = part.trim();
    if (!trimmed) return null;
    const boldPhrases = [
      "Message Frequency:",
      "Message & Data Rates:",
      "Opt-Out:",
      "Help:",
      "Consent Not Required for Purchase:",
    ];
    let bolded = trimmed;
    for (const phrase of boldPhrases) {
      if (trimmed.startsWith(phrase)) {
        const rest = trimmed.slice(phrase.length);
        bolded = (
          <>
            <strong>{phrase}</strong>
            {rest}
          </>
        );
        break;
      }
    }
    return (
      <span key={i} className="block">
        {bolded}
        {i < parts.length - 1 && " "}
      </span>
    );
  });

};

// Helper to highlight company name with brand color
const highlightCompanyName = (text) => {
  const parts = text.split(/(MK AI Technologies|MK AI TECHNOLOGIES)/g);
  return parts.map((part, i) =>
    /MK AI Technologies/i.test(part) ? (
      <span key={i} className="text-[#8B4AFA] font-semibold">
        {part}
      </span>
    ) : (
      part
    )
  );
};

const sections = [
  {
    id: "overview",
    icon: FileText,
    title: "1. Overview",
   body: 'MK AI Technologies ("Company", "we", "us", or "our") respects your privacy and is committed to protecting it through this Privacy Policy. This policy explains how we collect, use, disclose, and safeguard your information when you visit our website (mkaitechnology.com) or engage with our services, including AI solutions, software development services, and SMS communications.',
  },
  {
    id: "information-collect",
    icon: Database,
    title: "2. Information We Collect",
    body: "We may collect the following information:",
    list: [
      "Personal Information: Name, email address, phone number, company name, mailing address, and any information you provide through our contact forms or project inquiries.",
      "Mobile Information: Phone number provided for SMS communications.",
      "Usage Data: IP address, browser type, device information, pages visited, referral source, and time spent on pages.",
      "Cookies & Tracking Data: See Section 7 below.",
    ],
  },
  {
    id: "how-we-use",
    icon: Settings2,
    title: "3. How We Use Your Information",
    body: "We use your information to:",
    list: [
      "Provide and manage our services",
      "Respond to inquiries and project requests",
      "Deliver AI, software development, cloud, cybersecurity, blockchain, mobile, web, and other technology services",
      "Communicate with you regarding your account or inquiries",
      "Send SMS messages you have opted into",
      "Improve our website and services",
      "Analyze website performance and user experience",
      "Comply with legal obligations",
    ],
  },
  {
    id: "sms-consent",
    icon: MessageSquare,
    title: "4. SMS Communications & Consent",
    body: "By providing your mobile phone number and opting in, you expressly consent to receive automated and non-automated SMS messages from MK AI Technologies, including but not limited to:",
    list: [
      "Customer support messages",
      "Service updates and notifications",
      "Appointment reminders",
      "Project updates",
      "Promotional and marketing messages (if separately consented)",
    ],
    extra: "Message Frequency: May vary. Message & Data Rates: Standard message and data rates may apply. Opt-Out: You may opt out at any time by replying STOP to any message. Help: Reply HELP for assistance. Consent Not Required for Purchase: Your consent to receive SMS messages is not a condition of purchasing any goods or services.",
  },
  {
    id: "sharing",
    icon: Share2,
    title: "5. Sharing of Information",
    body: "We do not sell or rent your personal information. We may share information with:",
    list: [
      "Service providers assisting us in operating our business",
      "Cloud hosting and infrastructure providers",
      "Analytics and communication service providers",
      "Legal authorities if required by law",
    ],
    extra: "No mobile information will be shared with third parties or affiliates for marketing or promotional purposes. Information sharing with subcontractors in support services, such as customer service, is permitted. All other use case categories exclude text messaging originator opt-in data and consent; this information will not be shared with any third parties.",
  },
  {
    id: "security",
    icon: ShieldCheck,
    title: "6. Data Security",
    body: "We implement reasonable administrative, technical, and physical safeguards designed to protect your information. However, no method of transmission over the Internet is 100% secure.",
  },
  {
    id: "cookies",
    icon: Cookie,
    title: "7. Cookies & Tracking Technologies",
    body: "We use cookies and similar tracking technologies to:",
    list: [
      "Analyze website traffic and usage",
      "Improve user experience",
      "Understand user behavior",
      "Improve our services",
    ],
    extra: "You may control cookie settings through your browser preferences. Disabling cookies may affect website functionality.",
  },
  {
    id: "your-rights",
    icon: UserCheck,
    title: "8. Your Rights & Choices",
    body: "You have the right to:",
    list: [
      "Access, correct, or delete your personal information",
      "Opt out of marketing communications",
      "Withdraw SMS consent at any time by replying STOP",
    ],
    extra: "To exercise these rights, contact us using the information below.",
  },
  {
    id: "children",
    icon: Settings2,
    title: "9. Children's Privacy",
    body: "Our services are not intended for individuals under the age of 18. We do not knowingly collect information from minors.",
  },
  {
    id: "changes",
    icon: RefreshCw,
    title: "10. Changes to This Privacy Policy",
    body: "We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated Effective Date.",
  },
  {
    id: "contact",
    icon: Mail,
    title: "11. Contact Us",
    body: "If you have questions about this Privacy Policy or our SMS practices, contact us at:",
    contact: true,
  },
];

const PrivacyPolicy = () => {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 96;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  // Helper to remove numbering from a title for the Table of Contents
  const tocTitle = (title) => title.replace(/^\d+\.\s*/, "");

  return (
    <section className="mt-40 bg-white">
      {/* Hero */}
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          className="mx-auto max-w-2xl border-b border-dotted border-[#8B4AFA] text-md tracking-widest text-slate-700"
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={0}
        >
          Legal &amp; Compliance
        </motion.div>

        <motion.h1
          className="mx-auto mt-6 max-w-4xl font-display text-4xl md:text-5xl font-semibold tracking-tight text-slate-900 sm:text-6xl leading-tight"
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={0.15}
        >
          Privacy Policy
        </motion.h1>

       <motion.p
        className="mx-auto mt-6 max-w-3xl text-lg tracking-tight text-slate-600"
        variants={fadeUp}
        initial="hidden"
        animate="visible"
        custom={0.3}
      >
        Your privacy matters to us. This policy outlines the information
        we collect, how it's used and protected, and the rights you have
        in connection with our AI, software development, and SMS
        communication services.
      </motion.p>

        <motion.div
          className="mb-6 mt-6 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-4 py-1.5 text-xs text-slate-500"
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={0.4}
        >
          <Clock className="w-3.5 h-3.5" />
          Effective Date: August 5, 2026
        </motion.div>
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pb-24 pt-16">
        <div className="grid gap-10 lg:grid-cols-[380px_1fr]">
          {/* Table of Contents – without numbers */}
          <aside className="hidden lg:block">
            <div className="sticky top-28 rounded-2xl border border-slate-100 bg-slate-50/60 p-6">
              <div className="mb-5 text-xs font-semibold uppercase tracking-widest text-slate-400">
                Table of Contents
              </div>
              <nav className="space-y-0.5">
                {sections.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => scrollTo(s.id)}
                    className="group w-full rounded-lg px-4 py-2.5 text-left text-sm text-slate-600 transition-all duration-300 hover:bg-gradient-to-r hover:from-[#8B4AFA]/10 hover:to-[#8B4AFA]/5 hover:text-[#8B4AFA]"
                  >
                    <span className="flex items-center gap-3">
                      <span className="flex h-1.5 w-1.5 flex-shrink-0 rounded-full bg-slate-300 transition-colors duration-300 group-hover:bg-[#8B4AFA]" />
                      <span className="relative whitespace-nowrap">
                        {tocTitle(s.title)}
                        <span className="absolute -bottom-0.5 left-0 h-[2px] w-0 bg-[#8B4AFA] transition-all duration-300 group-hover:w-full" />
                      </span>
                    </span>
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* Main content – full titles with numbers */}
          <div>
            {sections.map((section, index) => {
              const Icon = section.icon;
              return (
                <motion.div
                  key={section.id}
                  id={section.id}
                  className="group mb-5 scroll-mt-28 rounded-2xl border border-slate-100 bg-white p-7 transition-all duration-300 hover:border-[#8B4AFA]/25 hover:shadow-lg hover:shadow-[#8B4AFA]/5 md:p-8 relative overflow-hidden"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: Math.min(index * 0.03, 0.3) }}
                >
                  {/* Left accent line on hover */}
                  <div className="absolute left-0 top-0 h-full w-1 bg-[#8B4AFA] opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

                  <div className="flex items-start gap-4 relative">
                    <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-[#8B4AFA]/10 transition-all duration-300 group-hover:bg-[#8B4AFA]/15 group-hover:scale-105">
                      <Icon className="w-5 h-5 text-[#8B4AFA]" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h2 className="mb-3 font-display text-xl font-semibold text-slate-900">
                        {section.title}
                      </h2>
                      {section.body && (
                        <p className="mb-3 text-base leading-relaxed text-slate-600">
                          {highlightCompanyName(section.body)}
                        </p>
                      )}
                      {section.list && (
                        <ul className="mt-2 space-y-2.5">
                          {section.list.map((item) => (
                            <li
                              key={item}
                              className="flex items-start gap-2.5 text-base leading-relaxed text-slate-600"
                            >
                              <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#8B4AFA]/50" />
                              <span>{boldPrefix(item)}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                      {section.extra && (
                        <div className="mt-3 text-base leading-relaxed text-slate-600">
                          {section.id === "sharing" ? (
                            <p className="font-semibold text-slate-800">{section.extra}</p>
                          ) : (
                            formatExtra(section.extra)
                          )}
                        </div>
                      )}
                      {section.contact && (
                        <div className="mt-4 rounded-2xl border border-[#8B4AFA]/10 bg-[#8B4AFA]/5 p-6">
                          <p className="mb-3 text-sm font-semibold text-slate-900">
                             MK AI Technologies
                          </p>
                          <div className="space-y-2.5">
                            <div className="flex items-center gap-2.5 text-sm text-slate-600">
                              <Mail className="w-4 h-4 text-[#8B4AFA]" />
                              <a
                                href="mailto:contact@mkaitechnology.com"
                                className="text-[#8B4AFA] hover:underline"
                              >
                                contact@mkaitechnology.com
                              </a>
                            </div>
                            <div className="flex items-center gap-2.5 text-sm text-slate-600">
                              <Globe className="w-4 h-4 text-[#8B4AFA]" />
                              <a
                                href="https://mkaitechnology.com"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[#8B4AFA] hover:underline"
                              >
                               mkaitechnology.com
                              </a>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default PrivacyPolicy;